/**
 * Class representing a select.
 */
export class ListBuilder {

  /**
   * Setup select properties.
   * @param {HTMLElement} element - The DOM element of select.
   */
  constructor(element, data = null) {
    this.container = element;
    this.entries = JSON.parse(JSON.stringify(data));

    this.listeners = {
      selectAll: (evt) => this._selectAll(evt),
      selectItem: (evt) => this._selectItem(evt),
      addItems: (evt) => this._addItems(evt),
      removeItems: (evt) => this._removeItems(evt),
      filterInputKeyup: (evt) => this._filterItems(evt)
    };

    this.filteredOptionsMain = [];
    this.filteredOptionsSecondary = [];

    this.output = [];
  }


  /**
   * Initialize select.
   * @public
   */
  init() {
    this._render();
    this._addEventListeners();
  }


  /**
   * Destroy list builder and associated events.
   * @public
   */
  destroy() {
    this._removeEventListeners();
  }


  /**
   * Create list builder
   * @private
   */
  _render(){
    const mainBlock = this.container.querySelector('.lb-main');
    this._generateItems(mainBlock, this.entries);
  }


  /**
   * Create list builder items
   * @private
   * @param {HTMLElement} place - current list builder block
   * @param {Array} items - array of items
   */
  _generateItems(place, items){
    const itemsContainer = place.querySelector('.lb-list');
    let itemsCounter = 0;
    const itemsCounterContainer = place.querySelector('.lb-counter-total');

    if (typeof items === 'object' && items !== null){

      // generate unique ID for each option input/label pair
      const uniqueName = Math.random().toString(36).substr(2, 5);

      for (let i = 0; i < items.length; i += 1){
        if (!items[i].disabled){

          const itemContainer = document.createElement('div');
          itemContainer.classList.add('lb-item');

          const checkbox = document.createElement('input');
          checkbox.addEventListener('change', this.listeners.selectItem);
          const label = document.createElement('label');
          const itemContent = items[i].title;

          checkbox.type = 'checkbox';
          checkbox.id = uniqueName + '_' + Number(i + 1);
          label.htmlFor = uniqueName + '_' + Number(i + 1);
          itemContainer.dataset.itemId = uniqueName + '_' + Number(i + 1);

          label.appendChild(document.createTextNode(itemContent));

          itemContainer.appendChild(checkbox);
          itemContainer.appendChild(label);
          itemsContainer.appendChild(itemContainer);

          itemsCounter += 1;
        }
      }
    }

    itemsCounterContainer.innerHTML =  itemsCounter;

    this._updateLists();
  }


  /**
   * Add event listeners.
   * @private
   */
  _addEventListeners() {
    this.container.querySelector('.lb-primary-cb-all').addEventListener('change', this.listeners.selectAll);
    this.container.querySelector('.lb-secondary-cb-all').addEventListener('change', this.listeners.selectAll);
    this.container.querySelector('.lb-add-btn').addEventListener('click', this.listeners.addItems);
    this.container.querySelector('.lb-remove-btn').addEventListener('click', this.listeners.removeItems);
    this.container.querySelector('.lb-filter-main').addEventListener('keyup', this.listeners.filterInputKeyup);
    this.container.querySelector('.lb-filter-secondary').addEventListener('keyup', this.listeners.filterInputKeyup);
  }


  /**
   * Remove event listeners.
   * @private
   */
  _removeEventListeners() {
    this.container.querySelector('.lb-primary-cb-all').removeEventListener('change', this.listeners.selectAll);
    this.container.querySelector('.lb-secondary-cb-all').removeEventListener('change', this.listeners.selectAll);
    this.container.querySelector('.lb-add-btn').removeEventListener('click', this.listeners.addItems);
    this.container.querySelector('.lb-remove-btn').removeEventListener('click', this.listeners.removeItems);
    this.container.querySelector('.lb-filter-main').removeEventListener('keyup', this.listeners.filterInputKeyup);
    this.container.querySelector('.lb-filter-secondary')
      .removeEventListener('keyup', this.listeners.filterInputKeyup);
  }


  /**
   * Select all items by clicking master checkbox
   * @private
   * @param {Event} event - a change event
   */
  _selectAll(evt){
    const mainCheckbox = evt.target;
    const listItems = mainCheckbox.closest('.lb-block').querySelectorAll('.lb-item');

    if (mainCheckbox.checked){
      listItems.forEach((listItem) => {
        if (window.getComputedStyle(listItem).display !== 'none'){
          listItem.firstChild.checked = true;
        }
      });
    } else {
      listItems.forEach((listItem) => {
        if (window.getComputedStyle(listItem).display !== 'none'){
          listItem.firstChild.checked = false;
        }
      });
    }

    this._updateSelectedCounter(mainCheckbox.closest('.lb-block'));
  }


  /**
   * Select one single item
   * @private
   * @param {Event} event - a change event.
   */
  _selectItem(evt){
    const targetItem = evt.target;

    this._updateSelectedCounter(targetItem.closest('.lb-block'));
  }


  /**
   * Update element counters
   * @private
   * @param {HTMLElement} place - current list builder block
   */
  _updateSelectedCounter(place){
    const number = place.querySelectorAll('.lb-item input:checked').length;

    place.querySelector('.lb-counter-selected').innerHTML = number;

    if (place.classList.contains('lb-main') && number > 0){
      this.container.querySelector('.lb-add-btn').disabled = false;
    } else {
      this.container.querySelector('.lb-add-btn').disabled = true;
    }

    if (place.classList.contains('lb-secondary') && number > 0){
      this.container.querySelector('.lb-remove-btn').disabled = false;
    } else {
      this.container.querySelector('.lb-remove-btn').disabled = true;
    }
  }


  /**
   * Show or hide needed items
   * @private
   */
  _updateLists(){
    const mainlist =  this.container.querySelector('.lb-main');
    const mainlistLength = mainlist.querySelectorAll('.lb-item').length;

    const secondarylist =  this.container.querySelector('.lb-secondary');
    const secondarylistLength = secondarylist.querySelectorAll('.lb-item').length;

    // update placeholder and main checkbox

    if (mainlistLength === 0){
      mainlist.querySelector('.lb-placeholder').style.display = 'block';
      mainlist.querySelector('.lb-header-cb').style.visibility = 'hidden';
      mainlist.querySelector('.lb-header-cb').firstChild.checked = false;
    } else {
      mainlist.querySelector('.lb-placeholder').style.display = 'none';
      mainlist.querySelector('.lb-header-cb').style.visibility = 'visible';
    }

    if (secondarylistLength === 0){
      secondarylist.querySelector('.lb-placeholder').style.display = 'block';
      secondarylist.querySelector('.lb-header-cb').style.visibility = 'hidden';
      secondarylist.querySelector('.lb-header-cb').firstChild.checked = false;
    } else {
      secondarylist.querySelector('.lb-placeholder').style.display = 'none';
      secondarylist.querySelector('.lb-header-cb').style.visibility = 'visible';
    }

    // update total number of items

    mainlist.querySelector('.lb-counter-total').innerHTML = mainlistLength;
    secondarylist.querySelector('.lb-counter-total').innerHTML = secondarylistLength;
  }


  /**
   * Transfer itmes from main block to secondary one
   * @private
   */
  _addItems(){
    const mainList = this.container.querySelector('.lb-main');
    const secondaryListContainer = this.container.querySelector('.lb-secondary .lb-list');

    mainList.querySelectorAll('.lb-item').forEach((mainListItem) => {
      if (mainListItem.firstChild.checked) {
        mainListItem.firstChild.checked = false;
        mainListItem.classList.add('highlight');
        secondaryListContainer.prepend(mainListItem);
        setTimeout(() => mainListItem.classList.remove('highlight'), 1000);
        this.output.push(mainListItem.getElementsByTagName('label')[0].innerText);
      }
    });

    mainList.querySelector('.lb-header-cb').firstChild.checked = false;
    this._updateSelectedCounter(this.container.querySelector('.lb-main'));
    this._updateLists();

    const targetBlockInput = secondaryListContainer.closest('.lb-block').querySelector('.lb-filter-input');
    const keyupEvt = new Event('keyup');

    targetBlockInput.dispatchEvent(keyupEvt);
  }


  /**
   * Transfer itmes from secondary block to main one
   * @private
   */
  _removeItems(){
    const secondaryList = this.container.querySelector('.lb-secondary');
    const mainListContainer = this.container.querySelector('.lb-main .lb-list');

    secondaryList.querySelectorAll('.lb-item').forEach((secondaryListItem) => {
      if (secondaryListItem.firstChild.checked) {
        secondaryListItem.firstChild.checked = false;

        secondaryListItem.classList.add('highlight');

        mainListContainer.prepend(secondaryListItem);
        setTimeout(() => secondaryListItem.classList.remove('highlight'), 1000);

        const index = this.output.indexOf(secondaryListItem.getElementsByTagName('label')[0].innerText);

        if (index > -1) {
          this.output.splice(index, 1);
        }
      }
    });

    secondaryList.querySelector('.lb-header-cb').firstChild.checked = false;
    this._updateSelectedCounter(this.container.querySelector('.lb-secondary'));
    this._updateLists();

    const targetBlockInput = mainListContainer.closest('.lb-block').querySelector('.lb-filter-input');
    const keyupEvt = new Event('keyup');

    targetBlockInput.dispatchEvent(keyupEvt);
  }


  /**
   * Filter items
   * @private
   * @param {Event} event - a key up event
   */
  _filterItems(evt) {
    const input = evt.target;
    const currentSection = input.closest('.lb-block');
    const items = Array.from(input.closest('.lb-block').querySelectorAll('.lb-item'));
    const typedValue = input.value.toLowerCase().trim();

    if (input.classList.contains('lb-filter-main')) {
      this.filteredOptionsMain = items.filter(val => val.innerText.toLowerCase().indexOf(typedValue) > -1);

      const totalItems = currentSection.querySelectorAll('.lb-item').length;
      const shownItems = this.filteredOptionsMain.length;

      if (typedValue){
        if (this.filteredOptionsMain.length){
          currentSection.querySelector('.lb-placeholder').style.display = 'none';

          currentSection.querySelectorAll('.lb-item').forEach((listItem) => {
            listItem.style.display = 'none';
          });

          this.filteredOptionsMain.forEach((listItem) => {
            currentSection.querySelector('[data-item-id=' + listItem.getAttribute('data-item-id') + ']')
              .style.display = 'block';
          });
        } else {
          currentSection.querySelectorAll('.lb-item').forEach((listItem) => {
            listItem.style.display = 'none';
          });

          currentSection.querySelector('.lb-placeholder').style.display = 'block';
        }

        currentSection.querySelector('.lb-counter-total').innerText = shownItems + ' results of ' + totalItems;
      } else {
        currentSection.querySelector('.lb-placeholder').style.display = 'none';
        currentSection.querySelector('.lb-counter-total').innerText = totalItems;

        currentSection.querySelectorAll('.lb-item').forEach((listItem) => {
          listItem.style.display = 'block';
        });
      }
    }

    if (input.classList.contains('lb-filter-secondary')) {
      this.filteredOptionsSecondary = items.filter(val => val.innerText.toLowerCase().indexOf(typedValue) > -1);

      const totalItems = currentSection.querySelectorAll('.lb-item').length;
      const shownItems = this.filteredOptionsSecondary.length;

      if (typedValue){
        if (this.filteredOptionsSecondary.length){
          currentSection.querySelector('.lb-placeholder').style.display = 'none';

          currentSection.querySelectorAll('.lb-item').forEach((listItem) => {
            listItem.style.display = 'none';
          });

          this.filteredOptionsSecondary.forEach((listItem) => {
            currentSection.querySelector('[data-item-id=' + listItem.getAttribute('data-item-id') + ']')
              .style.display = 'block';
          });
        } else {
          currentSection.querySelectorAll('.lb-item').forEach((listItem) => {
            listItem.style.display = 'none';
          });

          currentSection.querySelector('.lb-placeholder').style.display = 'block';
        }

        currentSection.querySelector('.lb-counter-total').innerText = shownItems + ' results of ' + totalItems;
      } else {
        currentSection.querySelector('.lb-placeholder').style.display = 'none';
        currentSection.querySelector('.lb-counter-total').innerText = totalItems;

        currentSection.querySelectorAll('.lb-item').forEach((listItem) => {
          listItem.style.display = 'block';
        });
      }
    }
  }
}
